'use client';

import React, { useState, useEffect } from 'react';
import { FaCalculator, FaCheckCircle, FaTimesCircle, FaChartLine, FaShieldAlt, FaCreditCard, FaDatabase, FaQuestionCircle } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import Chart from 'react-apexcharts';
import { useTheme } from 'next-themes';

const CheckDigitCalculator = () => {
  const [inputNumber, setInputNumber] = useState('');
  const [result, setResult] = useState(null);
  const [isValid, setIsValid] = useState(null);
  const [chartData, setChartData] = useState({});
  const { theme } = useTheme();

  const calculateCheckDigit = (number) => {
    const digits = number.split('').map(Number);
    let sum = 0;
    
    for (let i = digits.length - 1; i >= 0; i--) {
      if ((digits.length - i) % 2 === 1) {
        let doubled = digits[i] * 2;
        sum += doubled > 9 ? doubled - 9 : doubled;
      } else {
        sum += digits[i];
      }
    }

    const checkDigit = (10 - (sum % 10)) % 10;
    return checkDigit;
  };

  const validateNumber = (number) => {
    const digits = number.split('').map(Number);
    const checkDigit = calculateCheckDigit(digits.slice(0, -1).join(''));
    return checkDigit === digits[digits.length - 1];
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!inputNumber) {
      alert('Please enter a number');
      return;
    }

    const number = inputNumber.replace(/\D/g, ''); // Remove all non-digit characters
    
    if (number.length < 2) {
      alert('Please enter at least 2 digits');
      return;
    }

    const checkDigit = calculateCheckDigit(number);
    const isValidNumber = validateNumber(number);

    setResult(`Check Digit: ${checkDigit}`);
    setIsValid(isValidNumber);

    // Update chart data
    const digits = number.split('').map(Number);
    const digitValues = digits.map((digit, index) => ({
      x: `Digit ${index + 1}`,
      y: digit
    }));

    setChartData({
      options: {
        chart: {
          type: 'bar',
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 800
          }
        },
        theme: {
          mode: theme === 'dark' ? 'dark' : 'light'
        },
        plotOptions: {
          bar: {
            borderRadius: 4,
            horizontal: true
          }
        },
        dataLabels: {
          enabled: false
        },
        xaxis: {
          categories: digitValues.map(d => d.x)
        },
        yaxis: {
          title: {
            text: 'Digit Value'
          }
        },
        fill: {
          colors: ['#4CAF50', '#E91E63']
        },
        responsive: [{
          breakpoint: 480,
          options: {
            chart: {
              width: 300
            },
            legend: {
              position: 'bottom'
            }
          }
        }]
      },
      series: [
        {
          name: 'Digit Values',
          data: digitValues.map(d => d.y)
        }
      ]
    });
  };

  const features = [
    {
      icon: <FaShieldAlt className="text-blue-500" />,
      title: 'Error Detection',
      description: 'Detect errors in identification numbers before they cause issues'
    },
    {
      icon: <FaCreditCard className="text-green-500" />,
      title: 'Credit Card Validation',
      description: 'Verify the validity of credit card numbers instantly'
    },
    {
      icon: <FaDatabase className="text-purple-500" />,
      title: 'Data Integrity',
      description: 'Ensure data integrity in your systems and databases'
    },
    {
      icon: <FaChartLine className="text-yellow-500" />,
      title: 'Visual Analysis',
      description: 'Analyze digit patterns with interactive charts'
    }
  ];

  const benefits = [
    {
      title: 'Prevent Data Entry Errors',
      description: 'Catch typos and transpositions before they cause problems'
    },
    {
      title: 'Enhance Security',
      description: 'Add an extra layer of security to your data validation'
    },
    {
      title: 'Save Time',
      description: 'Quickly validate numbers without manual checks'
    },
    {
      title: 'Improve Accuracy',
      description: 'Reduce errors in data processing and transactions'
    }
  ];

  const faqs = [
    {
      question: 'What is a check digit?',
      answer: 'A check digit is a form of redundancy check used for error detection on identification numbers. It helps verify the integrity of the number.'
    },
    {
      question: 'How does the Luhn algorithm work?',
      answer: 'The Luhn algorithm doubles every second digit from the right, adds the digits, and calculates a check digit to verify the number.'
    },
    {
      question: 'Why is this important?',
      answer: 'Check digits help prevent data entry errors and ensure the integrity of identification numbers in systems like credit cards and bank accounts.'
    },
    {
      question: 'What numbers can I validate?',
      answer: 'You can validate any numerical identification number, including credit card numbers, bank account numbers, and other identification numbers.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 dark:from-gray-900 dark:to-gray-800">
      <Helmet>
        <title>Check Digit Calculator - Validate and Generate Check Digits</title>
        <meta name="description" content="Calculate and validate check digits using the Luhn algorithm. Enter your number to get the check digit and verify its validity." />
        <meta property="og:title" content="Check Digit Calculator" />
        <meta property="og:description" content="Calculate and validate check digits using the Luhn algorithm. Enter your number to get the check digit and verify its validity." />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://example.com/path-to-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Check Digit Calculator" />
        <meta name="twitter:description" content="Calculate and validate check digits using the Luhn algorithm. Enter your number to get the check digit and verify its validity." />
        <meta name="twitter:image" content="https://example.com/path-to-image.jpg" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="calculator-header text-center mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-2xl mx-auto"
          >
            <div className="flex items-center justify-center mb-6">
              <FaCalculator className="text-4xl text-blue-500 dark:text-blue-400" />
              <h2 className="text-2xl font-bold ml-3">Check Digit Calculator</h2>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="flex items-center border-2 border-gray-200 dark:border-gray-700 rounded-lg p-2">
                <FaCalculator className="text-blue-500 dark:text-blue-400" />
                <input
                  type="text"
                  placeholder="Enter number (e.g., 123456789)"
                  value={inputNumber}
                  onChange={(e) => setInputNumber(e.target.value)}
                  className="ml-3 w-full p-2 outline-none bg-gray-50 dark:bg-gray-900"
                />
              </div>

              <button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-500 to-green-500 text-green-500 dark:text-green-400 py-3 rounded-lg hover:from-blue-600 hover:to-green-600 transition duration-300"
              >
                Calculate Check Digit
              </button>

              {result && (
                <div className="bg-blue-100 dark:bg-blue-900 p-4 rounded-lg mt-4">
                  <h3 className="font-semibold mb-2">Result:</h3>
                  <p className="text-blue-800 dark:text-blue-400 text-lg">{result}</p>
                  {isValid !== null && (
                    <div className="mt-2">
                      <span className="font-semibold">Validation:</span>
                      {isValid ? (
                        <span className="text-green-500 ml-2">
                          <FaCheckCircle /> Valid
                        </span>
                      ) : (
                        <span className="text-red-500 ml-2">
                          <FaTimesCircle /> Invalid
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
            </form>
          </motion.div>
        </div>

        {/* Chart Section */}
        {Object.keys(chartData).length > 0 && (
          <div className="mt-12">
            <h3 className="text-2xl font-bold mb-6">Digit Analysis</h3>
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8">
              <Chart
                options={chartData.options}
                series={chartData.series}
                type="bar"
                width={600}
              />
            </div>
          </div>
        )}

        {/* Features Section */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold mb-8">Key Features</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 text-center"
              >
                {feature.icon}
                <h4 className="mt-3 font-semibold">{feature.title}</h4>
                <p className="mt-2 text-gray-600 dark:text-gray-400">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Benefits Section */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold mb-8">Benefits</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6"
              >
                <h4 className="font-semibold mb-2">{benefit.title}</h4>
                <p className="text-gray-600 dark:text-gray-400">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* How to Use Section */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold mb-8">How to Use</h3>
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6">
            <ol className="list-decimal list-inside space-y-4 text-gray-600 dark:text-gray-400">
              <li>Enter your number in the input field</li>
              <li>Click the "Calculate Check Digit" button</li>
              <li>View the calculated check digit and validation status</li>
              <li>Check the digit analysis chart for patterns</li>
            </ol>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold mb-8">Frequently Asked Questions</h3>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6"
              >
                <div className="flex items-center">
                  <FaQuestionCircle className="text-yellow-500 dark:text-yellow-400 mr-3" />
                  <h4 className="font-semibold">{faq.question}</h4>
                </div>
                <p className="mt-2 text-gray-600 dark:text-gray-400">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckDigitCalculator;
